/**
 * FileBrowser Component - Sidebar file tree for LaTeX projects
 * Displays hierarchical file structure with folders and files
 */

import React, { useState, useCallback, useRef, useEffect } from 'react'
import {
  FolderIcon,
  DocumentIcon,
  PlusIcon,
  EllipsisVerticalIcon,
  PhotoIcon,
  TrashIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  FolderArrowDownIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ArrowPathIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { logError, logDebug } from '@utils/logger'
import ConfirmDialog from '@components/common/ConfirmDialog'

import { latexAPI } from '../../api/latex'
import type { DocumentFile } from '../../types'
import { clearImageCache } from '@utils/imageAutocomplete'
import { FileVersionHistoryModal } from './FileVersionHistoryModal'

interface FileBrowserProps {
  documentId: string
  onFileSelect?: (file: DocumentFile) => void
  onFileCreate?: (file: DocumentFile) => void
  onFileDelete?: (fileId: string) => void
  onCollapse?: () => void
  refreshToken?: number
  className?: string
}

interface ImagePreviewModalProps {
  isOpen: boolean
  onClose: () => void
  imageUrl: string
  imageName: string
}

interface PDFPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  pdfUrl: string
  pdfName: string
}

interface FolderSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (folderId: string | null) => void  // Changed to folderId for move functionality
  folders: DocumentFile[]
  documentId: string
  title?: string
}

interface FileTreeNodeProps {
  file: DocumentFile
  level: number
  onFileSelect?: (file: DocumentFile) => void
  onFileDelete?: (fileId: string) => void
  onRename?: (file: DocumentFile, newName: string) => void
  onMove?: (file: DocumentFile | string, targetFolderId: string | null) => void
  onCopy?: (file: DocumentFile, targetFolderId: string | null) => void
  onDownload?: (file: DocumentFile) => void
  selectedFileId?: string
  dragOverFolder?: string | null
  onDragOverFolder?: (folderPath: string | null) => void
  draggedFileId?: string | null
  onDragStart?: (fileId: string) => void
  onDragEnd?: () => void
}

const FileTreeNode: React.FC<FileTreeNodeProps> = ({
  file,
  level,
  onFileSelect,
  onFileDelete,
  onRename,
  onMove,
  onCopy,
  onDownload,
  selectedFileId,
  dragOverFolder,
  onDragOverFolder,
  draggedFileId,
  onDragStart,
  onDragEnd
}) => {
  const [isExpanded, setIsExpanded] = useState(false) // All folders collapsed by default
  const [isRenaming, setIsRenaming] = useState(false)
  const [newName, setNewName] = useState(file.name)
  const [showMenu, setShowMenu] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const isSelected = selectedFileId === file.id
  const isFolder = file.file_type === 'folder'

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isRenaming])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (isFolder) {
      setIsExpanded(!isExpanded)
    } else {
      onFileSelect?.(file)
    }
  }, [isFolder, isExpanded, file, onFileSelect])

  const handleFileClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isFolder) {
      onFileSelect?.(file)
    }
  }, [isFolder, file, onFileSelect])

  const handleDragStart = useCallback((e: React.DragEvent) => {
    if (!isFolder && file.file_type === 'file') {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', file.id)
      onDragStart?.(file.id)
    }
  }, [isFolder, file.file_type, file.id, onDragStart])

  const handleDragEnd = useCallback(() => {
    onDragEnd?.()
  }, [onDragEnd])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (isFolder && draggedFileId && draggedFileId !== file.id) {
      e.preventDefault()
      e.stopPropagation()
      e.dataTransfer.dropEffect = 'move'
      onDragOverFolder?.(file.file_path)
    } else if (isFolder) {
      e.preventDefault()
      e.stopPropagation()
      onDragOverFolder?.(file.file_path)
    }
  }, [isFolder, file.file_path, file.id, draggedFileId, onDragOverFolder])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (isFolder) {
      e.preventDefault()
      e.stopPropagation()
      // Only clear if we're leaving the folder itself, not entering a child
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const x = e.clientX
      const y = e.clientY
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        onDragOverFolder?.(null)
      }
    }
  }, [isFolder, onDragOverFolder])

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (isFolder && draggedFileId && draggedFileId !== file.id && onMove) {
      e.preventDefault()
      e.stopPropagation()
      // Find the dragged file and move it to this folder
      // Pass the file ID as a string - the parent will find the file object
      const draggedFileData = e.dataTransfer.getData('text/plain')
      if (draggedFileData && onMove) {
        // Pass the file ID as a string - handleMoveFile will find the file
        onMove(draggedFileData, file.id)  // file here is the target folder
      }
      onDragOverFolder?.(null)
      onDragEnd?.()
    }
  }, [isFolder, file, draggedFileId, onMove, onDragOverFolder, onDragEnd])

  const isDragOver = dragOverFolder === file.file_path

  const handleRename = useCallback(async () => {
    if (newName.trim() && newName !== file.name && onRename) {
      try {
        await onRename(file, newName.trim())
        setIsRenaming(false)
        toast.success('File renamed successfully')
      } catch (error: any) {
        toast.error(error.response?.data?.detail || 'Failed to rename file')
        setNewName(file.name)
      }
    } else {
      setNewName(file.name)
      setIsRenaming(false)
    }
  }, [newName, file, onRename])

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true)
    setShowMenu(false)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true)
    setShowDeleteConfirm(false)
    try {
      await onFileDelete?.(file.id)
      toast.success('File deleted successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete file')
    } finally {
      setIsDeleting(false)
    }
  }, [file, onFileDelete])

  const getFileIcon = () => {
    if (isFolder) {
      return <FolderIcon className="w-5 h-5 text-blue-500" />
    }

    const ext = file.name.split('.').pop()?.toLowerCase()
    const mimeType = file.mime_type?.toLowerCase() || ''

    if (mimeType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(ext || '')) {
      return <PhotoIcon className="w-5 h-5 text-green-500" />
    }

    if (ext === 'tex' || ext === 'bib' || ext === 'sty' || ext === 'cls') {
      return <DocumentTextIcon className="w-5 h-5 text-orange-500" />
    }

    return <DocumentIcon className="w-5 h-5 text-gray-500" />
  }

  // Validate file structure
  if (!file || !file.id || !file.name) {
    logError('[FileTreeNode] Invalid file structure:', file)
    return null
  }

  const isDragging = draggedFileId === file.id

  return (
    <div>
      <div
        draggable={!isFolder && file.file_type === 'file'}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className={`
          flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer
          hover:bg-gray-100 dark:hover:bg-gray-800
          ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
          ${isDeleting ? 'opacity-50' : ''}
          ${isDragOver ? 'bg-blue-200 dark:bg-blue-800/50 border-2 border-blue-400 border-dashed' : ''}
          ${isDragging ? 'opacity-50' : ''}
        `}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div onClick={handleToggle} className="flex items-center gap-2 flex-1 min-w-0">
          {isFolder && (
            <div className="w-4 h-4 flex items-center justify-center">
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-500" />
              )}
            </div>
          )}
          {!isFolder && <div className="w-4 h-4" />}
          {getFileIcon()}
          
          {isRenaming ? (
          <input
            ref={inputRef}
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename()
              if (e.key === 'Escape') {
                setNewName(file.name)
                setIsRenaming(false)
              }
            }}
            className="flex-1 px-1 py-0.5 text-sm border border-blue-500 rounded"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            <span 
              className="flex-1 text-sm truncate cursor-pointer text-gray-900 dark:text-gray-100" 
              title={file.name}
              onClick={handleFileClick}
            >
              {file.name}
              {file.is_main_tex && (
                <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(main)</span>
              )}
            </span>
          </>
        )}
        </div>
        
        {!isRenaming && (
          <div className="relative" ref={menuRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowMenu(!showMenu)
                }}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              >
                <EllipsisVerticalIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
              </button>

              <AnimatePresence>
                {showMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!isFolder && (
                      <>
                        <button
                          onClick={() => {
                            setIsRenaming(true)
                            setShowMenu(false)
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <PencilIcon className="w-4 h-4" />
                          Rename
                        </button>
                        <button
                          onClick={() => {
                            onDownload?.(file)
                            setShowMenu(false)
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                          Download
                        </button>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                        <button
                          onClick={() => {
                            onMove?.(file, null)  // null means show modal
                            setShowMenu(false)
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <FolderArrowDownIcon className="w-4 h-4" />
                          Move
                        </button>
                        <button
                          onClick={() => {
                            onCopy?.(file, null)  // null means show modal
                            setShowMenu(false)
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                          Copy
                        </button>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                        <button
                          onClick={() => {
                            // Pass file info to parent to open version history modal
                            const event = new CustomEvent('openVersionHistory', {
                              detail: { fileId: file.id, fileName: file.name }
                            })
                            window.dispatchEvent(event)
                            setShowMenu(false)
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <ClockIcon className="w-4 h-4" />
                          Version History
                        </button>
                      </>
                    )}
                    {isFolder && (
                      <>
                        <button
                          onClick={() => {
                            onMove?.(file, null)  // null means show modal
                            setShowMenu(false)
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <FolderArrowDownIcon className="w-4 h-4" />
                          Move
                        </button>
                        <button
                          onClick={() => {
                            onCopy?.(file, null)  // null means show modal
                            setShowMenu(false)
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                          Copy
                        </button>
                      </>
                    )}
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                    <button
                      onClick={handleDeleteClick}
                      disabled={isDeleting}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                    >
                      <TrashIcon className="w-4 h-4" />
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete File"
        message={`Are you sure you want to delete "${file.name}"?${isFolder ? ' This will delete all contents inside this folder.' : ' This action cannot be undone.'}`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {isFolder && isExpanded && file.children && file.children.length > 0 && (
        <div className="mt-1">
          {file.children.map((child) => (
            <FileTreeNode
              key={child.id}
              file={child}
              level={level + 1}
              onFileSelect={onFileSelect}
              onFileDelete={onFileDelete}
              onRename={onRename}
              onMove={onMove}
              onCopy={onCopy}
              onDownload={onDownload}
              selectedFileId={selectedFileId}
              dragOverFolder={dragOverFolder}
              onDragOverFolder={onDragOverFolder}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Helper function to collect all folders from file tree
const collectFolders = (files: DocumentFile[]): DocumentFile[] => {
  const folders: DocumentFile[] = []
  const traverse = (items: DocumentFile[]) => {
    for (const item of items) {
      if (item.file_type === 'folder') {
        folders.push(item)
        if (item.children && item.children.length > 0) {
          traverse(item.children)
        }
      }
    }
  }
  traverse(files)
  return folders
}

// Image Preview Modal Component
const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  imageName
}) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="max-w-4xl max-h-[90vh] mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
              {imageName}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <img
              src={imageUrl}
              alt={imageName}
              className="max-w-full max-h-[70vh] object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// PDF Preview Modal Component
const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  isOpen,
  onClose,
  pdfUrl,
  pdfName
}) => {
  const [size, setSize] = React.useState({ width: 90, height: 90 }) // Percentage of viewport
  const [isResizing, setIsResizing] = React.useState(false)
  const [resizeType, setResizeType] = React.useState<'width' | 'height' | 'both' | null>(null)
  const modalRef = React.useRef<HTMLDivElement>(null)
  const startPosRef = React.useRef<{ x: number; y: number; width: number; height: number } | null>(null)

  React.useEffect(() => {
    if (!isOpen) {
      // Reset size when modal closes
      setSize({ width: 90, height: 90 })
    }
  }, [isOpen])

  const handleMouseDown = React.useCallback((e: React.MouseEvent, type: 'width' | 'height' | 'both') => {
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(true)
    setResizeType(type)
    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect()
      startPosRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height
      }
    }
  }, [])

  React.useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!startPosRef.current || !modalRef.current) return

      const deltaX = e.clientX - startPosRef.current.x
      const deltaY = e.clientY - startPosRef.current.y
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      if (resizeType === 'width' || resizeType === 'both') {
        const newWidth = ((startPosRef.current.width + deltaX) / viewportWidth) * 100
        setSize(prev => ({ ...prev, width: Math.max(50, Math.min(95, newWidth)) }))
      }

      if (resizeType === 'height' || resizeType === 'both') {
        const newHeight = ((startPosRef.current.height + deltaY) / viewportHeight) * 100
        setSize(prev => ({ ...prev, height: Math.max(50, Math.min(95, newHeight)) }))
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      setResizeType(null)
      startPosRef.current = null
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, resizeType])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden flex flex-col relative"
        style={{
          width: `${size.width}vw`,
          height: `${size.height}vh`,
          maxWidth: '95vw',
          maxHeight: '95vh',
          minWidth: '50vw',
          minHeight: '50vh'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Resize handles */}
        <div
          className="absolute top-0 right-0 w-4 h-4 cursor-nwse-resize z-10 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500"
          onMouseDown={(e) => handleMouseDown(e, 'both')}
          style={{ borderBottomLeftRadius: '0.5rem' }}
        />
        <div
          className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize z-10 hover:bg-gray-300 dark:hover:bg-gray-600"
          onMouseDown={(e) => handleMouseDown(e, 'height')}
        />
        <div
          className="absolute top-0 bottom-0 right-0 w-2 cursor-ew-resize z-10 hover:bg-gray-300 dark:hover:bg-gray-600"
          onMouseDown={(e) => handleMouseDown(e, 'width')}
        />

        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            {pdfName}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* PDF Content */}
        <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-900">
          <object
            data={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
            type="application/pdf"
            className="w-full h-full"
            aria-label={pdfName}
          >
            <iframe
              src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
              className="w-full h-full border-0"
              title={pdfName}
              type="application/pdf"
            />
          </object>
        </div>
      </div>
    </div>
  )
}

// Folder Selection Modal Component
const FolderSelectionModal: React.FC<FolderSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  folders,
  title = "Select Location"
}) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
        </div>
        <div className="p-4 max-h-96 overflow-y-auto">
          <button
            onClick={() => {
              onSelect(null)
              onClose()
            }}
            className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded mb-2 flex items-center gap-2"
          >
            <FolderIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            <span className="font-medium text-gray-900 dark:text-gray-100">Root (base folder)</span>
          </button>
          {folders.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
              No folders available. Create a folder first.
            </p>
          ) : (
            folders.map((folder) => (
              <button
                key={folder.id}
                onClick={() => {
                  onSelect(folder.id)
                  onClose()
                }}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded mb-2 flex items-center gap-2"
              >
                <FolderIcon className="w-5 h-5 text-blue-500" />
                <span className="font-medium text-gray-900 dark:text-gray-100">{folder.file_path}</span>
              </button>
            ))
          )}
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export const FileBrowser: React.FC<FileBrowserProps> = ({
  documentId,
  onFileSelect,
  onFileCreate,
  onFileDelete,
  onCollapse,
  refreshToken,
  className = ''
}) => {
  const [files, setFiles] = useState<DocumentFile[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedFileId, setSelectedFileId] = useState<string>()
  const [showCreateMenu, setShowCreateMenu] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [showFolderModal, setShowFolderModal] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null)
  const [imagePreview, setImagePreview] = useState<{ url: string; name: string } | null>(null)
  const [pdfPreview, setPdfPreview] = useState<{ url: string; name: string } | null>(null)
  const [moveFileModal, setMoveFileModal] = useState<{ file: DocumentFile | null; isOpen: boolean }>({ file: null, isOpen: false })
  const [copyFileModal, setCopyFileModal] = useState<{ file: DocumentFile | null; isOpen: boolean }>({ file: null, isOpen: false })
  const [overwriteConfirm, setOverwriteConfirm] = useState<{ 
    isOpen: boolean
    fileName: string
    fileId: string
    file: File
    targetPath: string | null
  }>({
    isOpen: false,
    fileName: '',
    fileId: '',
    file: null as any,
    targetPath: null
  })
  const [versionHistoryModal, setVersionHistoryModal] = useState<{
    isOpen: boolean
    fileId: string
    fileName: string
  }>({
    isOpen: false,
    fileId: '',
    fileName: ''
  })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const createMenuRef = useRef<HTMLDivElement>(null)
  const lastRefreshTokenRef = useRef<number | undefined>(undefined)

  // Listen for version history open event
  useEffect(() => {
    const handleOpenVersionHistory = (event: Event) => {
      const customEvent = event as CustomEvent<{ fileId: string; fileName: string }>
      setVersionHistoryModal({
        isOpen: true,
        fileId: customEvent.detail.fileId,
        fileName: customEvent.detail.fileName
      })
    }

    window.addEventListener('openVersionHistory', handleOpenVersionHistory)
    return () => {
      window.removeEventListener('openVersionHistory', handleOpenVersionHistory)
    }
  }, [])

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true)
      const response = await latexAPI.getFileTree(documentId)
      // console.log('[FileBrowser] File tree response:', response)
      // console.log('[FileBrowser] Files array:', response.files)
      // console.log('[FileBrowser] Files count:', response.files?.length)
      if (response.files && Array.isArray(response.files)) {
        setFiles(response.files)
      } else {
        logError('[FileBrowser] Invalid response format:', response)
        setFiles([])
      }
    } catch (error: any) {
      logError('[FileBrowser] Error loading files:', error)
      toast.error(error.response?.data?.detail || 'Failed to load files')
      setFiles([])
    } finally {
      setLoading(false)
    }
  }, [documentId])

  useEffect(() => {
    loadFiles()
  }, [loadFiles])

  useEffect(() => {
    if (refreshToken === undefined) return
    if (lastRefreshTokenRef.current === undefined) {
      lastRefreshTokenRef.current = refreshToken
      return
    }
    if (refreshToken !== lastRefreshTokenRef.current) {
      lastRefreshTokenRef.current = refreshToken
      loadFiles()
    }
  }, [refreshToken, loadFiles])

  // Debug: Log files whenever they change
  useEffect(() => {
    // console.log('[FileBrowser] Files state updated:', files)
    // console.log('[FileBrowser] Files count:', files?.length)
    if (files && files.length > 0) {
      logDebug('[FileBrowser] First file:', files[0])
    }
  }, [files])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(event.target as Node)) {
        setShowCreateMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleFileSelect = useCallback(async (file: DocumentFile) => {
    if (file.file_type === 'file') {
      setSelectedFileId(file.id)
      
      // Check if it's an image file
      const isImage = file.mime_type?.startsWith('image/') || 
                     ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(
                       file.name.split('.').pop()?.toLowerCase() || ''
                     )
      
      // Check if it's a PDF file
      const isPDF = file.mime_type === 'application/pdf' || 
                   file.name.split('.').pop()?.toLowerCase() === 'pdf'
      
      if (isImage) {
        // Show image preview
        try {
          const blob = await latexAPI.getFileContent(documentId, file.id)
          const imageUrl = URL.createObjectURL(blob)
          setImagePreview({ url: imageUrl, name: file.name })
        } catch (error: any) {
          toast.error(error.response?.data?.detail || 'Failed to load image')
        }
      } else if (isPDF) {
        // Show PDF preview
        try {
          // Fetch PDF as blob and create object URL with proper MIME type
          const blob = await latexAPI.getFileContent(documentId, file.id)
          // Ensure blob has correct MIME type for PDF
          const pdfBlob = new Blob([blob], { type: 'application/pdf' })
          const pdfUrl = URL.createObjectURL(pdfBlob)
          setPdfPreview({ url: pdfUrl, name: file.name })
        } catch (error: any) {
          toast.error(error.response?.data?.detail || 'Failed to load PDF')
        }
      } else {
        // For text files, call the original callback
        onFileSelect?.(file)
      }
    }
  }, [documentId, onFileSelect])
  
  const handleFileDownload = useCallback(async (file: DocumentFile) => {
    if (file.file_type === 'file') {
      try {
        const blob = await latexAPI.getFileContent(documentId, file.id)
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = file.name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        toast.success(`Downloaded ${file.name}`)
      } catch (error: any) {
        toast.error(error.response?.data?.detail || 'Failed to download file')
      }
    }
  }, [documentId])

  const handleFileDelete = useCallback(async (fileId: string) => {
    try {
      await latexAPI.deleteFile(documentId, fileId)
      await loadFiles()
      if (selectedFileId === fileId) {
        setSelectedFileId(undefined)
      }
      onFileDelete?.(fileId)
    } catch (error: any) {
      throw error // Re-throw to let FileTreeNode handle the error
    }
  }, [documentId, loadFiles, selectedFileId, onFileDelete])

  // Helper function to recursively find a file by ID
  const findFileById = useCallback((fileList: DocumentFile[], fileId: string): DocumentFile | null => {
    for (const file of fileList) {
      if (file.id === fileId) {
        return file
      }
      if (file.children && file.children.length > 0) {
        const found = findFileById(file.children, fileId)
        if (found) return found
      }
    }
    return null
  }, [])

  const handleRename = useCallback(async (file: DocumentFile, newName: string) => {
    await latexAPI.updateFile(documentId, file.id, { name: newName })
    await loadFiles()
  }, [documentId, loadFiles])

  const handleMoveFile = useCallback(async (file: DocumentFile | string, targetFolderId: string | null | undefined) => {
    // If file is a string (ID), find the actual file object
    let fileObj: DocumentFile | null = null
    if (typeof file === 'string') {
      fileObj = findFileById(files, file)
      if (!fileObj) {
        toast.error('File not found')
        return
      }
    } else {
      fileObj = file
    }

    // If targetFolderId is null or undefined, show the modal
    if (targetFolderId === null || targetFolderId === undefined) {
      // Show folder selection modal
      setMoveFileModal({ file: fileObj, isOpen: true })
      return
    }

    try {
      // If targetFolderId is empty string, move to root (use empty string for parent_id)
      // Empty string will be converted to null on the backend
      const parentId = targetFolderId === '' ? '' : targetFolderId
      await latexAPI.updateFile(documentId, fileObj.id, { parent_id: parentId })
      await loadFiles()
      toast.success(`Moved ${fileObj.name} successfully`)
      setMoveFileModal({ file: null, isOpen: false })
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to move file')
    }
  }, [documentId, loadFiles, files, findFileById])

  const handleCopyFile = useCallback(async (file: DocumentFile, targetFolderId: string | null | undefined) => {
    // If targetFolderId is null or undefined, show the modal
    if (targetFolderId === null || targetFolderId === undefined) {
      // Show folder selection modal
      setCopyFileModal({ file, isOpen: true })
      return
    }

    try {
      // Get file content
      const content = await latexAPI.getFileContent(documentId, file.id)
      
      // Determine new file path
      let newFilePath: string
      if (targetFolderId && targetFolderId !== '') {
        // Find the target folder to get its path
        const findFolderById = (files: DocumentFile[], id: string): DocumentFile | null => {
          for (const f of files) {
            if (f.id === id) return f
            if (f.children) {
              const found = findFolderById(f.children, id)
              if (found) return found
            }
          }
          return null
        }
        const targetFolder = findFolderById(files, targetFolderId)
        const folderPath = targetFolder?.file_path || ''
        newFilePath = folderPath ? `${folderPath}/${file.name}` : file.name
      } else {
        // Copy to root
        newFilePath = file.name
      }

      // Create a copy by uploading the content as a new file
      const blob = new Blob([content], { type: file.mime_type || 'application/octet-stream' })
      const fileObj = new File([blob], file.name, { type: file.mime_type || 'application/octet-stream' })
      
      await latexAPI.uploadFile(documentId, fileObj, newFilePath, file.is_main_tex)
      await loadFiles()
      toast.success(`Copied ${file.name} successfully`)
      setCopyFileModal({ file: null, isOpen: false })
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to copy file')
    }
  }, [documentId, loadFiles, files])


  const collectFoldersForMove = useCallback((files: DocumentFile[], excludeFileId?: string): DocumentFile[] => {
    const folders: DocumentFile[] = []
    const excludeId = excludeFileId || moveFileModal.file?.id || copyFileModal.file?.id
    
    const collect = (fileList: DocumentFile[]) => {
      for (const file of fileList) {
        if (file.file_type === 'folder' && file.id !== excludeId) {
          folders.push(file)
          if (file.children) {
            collect(file.children)
          }
        }
      }
    }
    collect(files)
    return folders
  }, [moveFileModal.file, copyFileModal.file])

  const handleCreateFile = useCallback(async () => {
    const name = prompt('Enter file name (e.g., main.tex, chapter1.tex, references.bib):')
    if (!name) return

    // Determine file type based on extension
    const ext = name.split('.').pop()?.toLowerCase()
    let content = ''
    
    // Set default content based on file type
    if (ext === 'tex') {
      content = '\\documentclass{article}\n\\begin{document}\n\n\\end{document}'
    } else if (ext === 'bib') {
      content = '% Bibliography file\n'
    } else if (ext === 'sty') {
      content = '% LaTeX style file\n\\NeedsTeXFormat{LaTeX2e}\n\\ProvidesPackage{' + name.replace('.sty', '') + '}\n'
    } else if (ext === 'cls') {
      content = '% LaTeX class file\n\\NeedsTeXFormat{LaTeX2e}\n\\ProvidesClass{' + name.replace('.cls', '') + '}\n'
    }

    try {
      logDebug('[FileBrowser] Creating file:', { name, file_path: name, file_type: 'file', contentLength: content.length })
      const newFile = await latexAPI.createFile(documentId, {
        name,
        file_path: name,
        file_type: 'file',
        content,
        is_main_tex: ext === 'tex' && (name.toLowerCase().includes('main') || name === 'document.tex')
      })
      logDebug('[FileBrowser] File created successfully:', newFile)
      await loadFiles()
      onFileCreate?.(newFile)
      toast.success('File created successfully')
    } catch (error: any) {
      logError('[FileBrowser] Error creating file:', error)
      logError('[FileBrowser] Error response:', error.response?.data)
      toast.error(error.response?.data?.detail || error.message || 'Failed to create file')
    }
    setShowCreateMenu(false)
  }, [documentId, loadFiles, onFileCreate])

  const handleCreateFolder = useCallback(async () => {
    const name = prompt('Enter folder name:')
    if (!name) return

    try {
      logDebug('[FileBrowser] Creating folder:', { name, file_path: name, file_type: 'folder' })
      const newFolder = await latexAPI.createFile(documentId, {
        name,
        file_path: name,
        file_type: 'folder'
      })
      logDebug('[FileBrowser] Folder created successfully:', newFolder)
      await loadFiles()
      onFileCreate?.(newFolder)
      toast.success('Folder created successfully')
    } catch (error: any) {
      logError('[FileBrowser] Error creating folder:', error)
      logError('[FileBrowser] Error response:', error.response?.data)
      toast.error(error.response?.data?.detail || error.message || 'Failed to create folder')
    }
    setShowCreateMenu(false)
  }, [documentId, loadFiles, onFileCreate])

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
    if (!selectedFiles || selectedFiles.length === 0) return

    // Collect all folders for selection
    const folders = collectFolders(files)
    
    // If folders exist, show modal to select target folder
    if (folders.length > 0) {
      setPendingFiles(Array.from(selectedFiles))
      setShowFolderModal(true)
    } else {
      // No folders, upload directly to root
      await uploadFiles(Array.from(selectedFiles), null)
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [documentId, files])

  const uploadFiles = useCallback(async (filesToUpload: File[], targetPath: string | null, skipConfirmation: boolean = false) => {
    setIsUploading(true)
    try {
      // Use batch upload for multiple files, single upload for one file
      if (filesToUpload.length === 1) {
        // Single file upload - use original endpoint for backward compatibility
        const file = filesToUpload[0]
        const finalPath = targetPath ? `${targetPath}/${file.name}` : file.name
        try {
          await latexAPI.uploadFile(documentId, file, finalPath)
          // Clear image cache so autocomplete shows new images
          clearImageCache(documentId)
          await loadFiles()
          toast.success('File uploaded successfully')
        } catch (error: any) {
          // Check if this is a "file already exists" error (409 Conflict)
          if (error.response?.status === 409 && error.response?.data?.detail?.error === 'file_exists') {
            const detail = error.response.data.detail
            // If skipConfirmation is true, directly overwrite
            if (skipConfirmation && detail.file_id) {
              await latexAPI.updateFileContent(documentId, detail.file_id, file)
              toast.success(`File "${file.name}" overwritten successfully`)
              await loadFiles()
              return
            }
            // Show confirmation dialog
            setOverwriteConfirm({
              isOpen: true,
              fileName: detail.file_name || file.name,
              fileId: detail.file_id,
              file: file,
              targetPath: targetPath
            })
            setIsUploading(false)
            return
          }
          // For other errors, re-throw to show error message
          throw error
        }
      } else {
        // Multiple files - use batch upload endpoint
        const batchResult = await latexAPI.uploadFilesBatch(
          documentId,
          filesToUpload,
          targetPath || undefined
        )
        
        // Process results
        const filesNeedingConfirmation: Array<{
          file: File
          fileId: string
          fileName: string
          filePath: string
        }> = []
        
        const successfulFiles: string[] = []
        const failedFiles: Array<{ name: string; error: string }> = []
        
        for (const result of batchResult.results) {
          if (result.success && result.file) {
            successfulFiles.push(result.filename)
          } else if (result.error === 'file_exists' && result.file_id) {
            // File exists - need confirmation
            const file = filesToUpload.find(f => f.name === result.filename)
            if (file) {
              filesNeedingConfirmation.push({
                file,
                fileId: result.file_id,
                fileName: result.file_name || result.filename,
                filePath: result.file_path || result.filename
              })
            }
          } else {
            // Other error
            failedFiles.push({
              name: result.filename,
              error: result.error || 'Unknown error'
            })
          }
        }
        
        // Handle files needing confirmation
        if (filesNeedingConfirmation.length > 0 && !skipConfirmation) {
          // Show confirmation for first file that needs it
          const firstFile = filesNeedingConfirmation[0]
          // Store remaining files that also need confirmation (for after first confirmation)
          const remainingFiles = filesNeedingConfirmation.slice(1).map(f => f.file)
          setPendingFiles(remainingFiles)
          
          setOverwriteConfirm({
            isOpen: true,
            fileName: firstFile.fileName,
            fileId: firstFile.fileId,
            file: firstFile.file,
            targetPath: targetPath
          })
          setIsUploading(false)
          return
        } else if (filesNeedingConfirmation.length > 0 && skipConfirmation) {
          // Auto-overwrite all files that need confirmation
          for (const fileInfo of filesNeedingConfirmation) {
            try {
              await latexAPI.updateFileContent(documentId, fileInfo.fileId, fileInfo.file)
              successfulFiles.push(fileInfo.fileName)
            } catch (error: any) {
              failedFiles.push({
                name: fileInfo.fileName,
                error: error.response?.data?.detail || error.message || 'Failed to overwrite'
              })
            }
          }
        }
        
        // Show results
        if (successfulFiles.length > 0) {
          toast.success(`${successfulFiles.length} file(s) uploaded successfully`)
          // Clear image cache so autocomplete shows new images
          clearImageCache(documentId)
        }
        if (failedFiles.length > 0) {
          const errorMessages = failedFiles.map(f => `${f.name}: ${f.error}`).join(', ')
          toast.error(`Failed to upload ${failedFiles.length} file(s): ${errorMessages}`)
        }
        
        await loadFiles()
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail?.message || error.response?.data?.detail || error.message || 'Failed to upload file(s)'
      toast.error(errorMessage)
      logError('[FileBrowser] Upload error:', error)
    } finally {
      setIsUploading(false)
      setPendingFiles([])
    }
  }, [documentId, loadFiles])
  
  const handleOverwriteConfirm = useCallback(async () => {
    if (!overwriteConfirm.fileId || !overwriteConfirm.file) {
      setOverwriteConfirm({
        isOpen: false,
        fileName: '',
        fileId: '',
        file: null as any,
        targetPath: null
      })
      return
    }
    
    setIsUploading(true)
    const currentTargetPath = overwriteConfirm.targetPath
    const remainingFiles = [...pendingFiles] // Copy remaining files
    
    try {
      // Update the existing file with new content
      await latexAPI.updateFileContent(documentId, overwriteConfirm.fileId, overwriteConfirm.file)
      toast.success(`File "${overwriteConfirm.fileName}" overwritten successfully`)
      
      // Clear image cache so autocomplete shows updated images
      clearImageCache(documentId)
      
      // Close the dialog first
      setOverwriteConfirm({
        isOpen: false,
        fileName: '',
        fileId: '',
        file: null as any,
        targetPath: null
      })
      
      // Continue with remaining files if any (skip confirmation for subsequent duplicates)
      if (remainingFiles.length > 0) {
        await uploadFiles(remainingFiles, currentTargetPath, true)
      } else {
        // No more files, just reload
        await loadFiles()
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to overwrite file'
      toast.error(errorMessage)
      logError('[FileBrowser] Overwrite error:', error)
      setIsUploading(false)
      setOverwriteConfirm({
        isOpen: false,
        fileName: '',
        fileId: '',
        file: null as any,
        targetPath: null
      })
      setPendingFiles([])
    }
  }, [overwriteConfirm, documentId, loadFiles, pendingFiles, uploadFiles])
  
  const handleOverwriteCancel = useCallback(() => {
    setOverwriteConfirm({
      isOpen: false,
      fileName: '',
      fileId: '',
      file: null as any,
      targetPath: null
    })
    setIsUploading(false)
    setPendingFiles([])
  }, [])

  const handleFolderSelect = useCallback((folderIdOrPath: string | null) => {
    // For uploads, we need to convert folder ID to path
    let folderPath: string | null = null
    if (folderIdOrPath) {
      // Find folder by ID to get its path
      const findFolderById = (files: DocumentFile[], id: string): DocumentFile | null => {
        for (const f of files) {
          if (f.id === id) return f
          if (f.children) {
            const found = findFolderById(f.children, id)
            if (found) return found
          }
        }
        return null
      }
      const folder = findFolderById(files, folderIdOrPath)
      folderPath = folder?.file_path || null
    }
    
    if (pendingFiles.length > 0) {
      uploadFiles(pendingFiles, folderPath)
    }
    setShowFolderModal(false)
  }, [pendingFiles, uploadFiles, files])

  // Helper function to recursively find a folder by path
  const findFolderByPath = useCallback((fileList: DocumentFile[], path: string): DocumentFile | null => {
    for (const file of fileList) {
      if (file.file_path === path && file.file_type === 'folder') {
        return file
      }
      if (file.children) {
        const found = findFolderByPath(file.children, path)
        if (found) return found
      }
    }
    return null
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Check if we're dropping a file from the file browser (moving)
    const draggedFileId = e.dataTransfer.getData('text/plain')
    if (draggedFileId && draggedFileId !== '') {
      // This is a file move operation - recursively find the file
      const draggedFile = findFileById(files, draggedFileId)
      
      if (draggedFile) {
        if (dragOverFolder && dragOverFolder !== '') {
          // Find the target folder by path
          const targetFolder = findFolderByPath(files, dragOverFolder)
          if (targetFolder) {
            await handleMoveFile(draggedFile, targetFolder.id)
          }
        } else {
          // Dropped on root area - move to root
          await handleMoveFile(draggedFile, '')
        }
      }
      setDragOverFolder(null)
      return
    }

    // Otherwise, handle file upload
    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length === 0) return

    // Use dragOverFolder if set, otherwise upload to root
    const targetPath = dragOverFolder && dragOverFolder !== '' ? dragOverFolder : null
    await uploadFiles(droppedFiles, targetPath)
    setDragOverFolder(null)
  }, [documentId, dragOverFolder, uploadFiles, files, handleMoveFile, findFileById, findFolderByPath])

  return (
    <div
      className={`flex flex-col h-full bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 overflow-hidden ${className}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-2 border-b border-gray-200 dark:border-gray-800 flex-shrink-0 min-w-0 relative z-10">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          {onCollapse && (
            <button
              onClick={onCollapse}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded flex-shrink-0"
              title="Collapse file browser"
            >
              <ChevronLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          )}
          <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">
            Project Files
          </h3>
        </div>
        
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={() => loadFiles()}
            disabled={loading}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh file tree"
          >
            <ArrowPathIcon className={`w-4 h-4 text-gray-700 dark:text-gray-300 ${loading ? 'animate-spin' : ''}`} />
          </button>
          
          <div className="relative" ref={createMenuRef}>
            <button
              onClick={() => setShowCreateMenu(!showCreateMenu)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              title="Create file or folder"
            >
              <PlusIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
            </button>

          <AnimatePresence>
            {showCreateMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50"
              >
                <button
                  onClick={handleCreateFile}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <DocumentIcon className="w-4 h-4" />
                  New File
                </button>
                <button
                  onClick={handleCreateFolder}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <FolderIcon className="w-4 h-4" />
                  New Folder
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  Upload File
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileUpload}
        />
      </div>

      {/* File Tree */}
      <div 
        className="flex-1 overflow-y-auto overflow-x-hidden p-2 min-h-0"
        onDragOver={(e) => {
          e.preventDefault()
          e.stopPropagation()
          // Allow dropping on the root area - set empty string to indicate root
          setDragOverFolder('')
        }}
        onDrop={async (e) => {
          e.preventDefault()
          e.stopPropagation()
          
          // Check if we're dropping a file from the file browser (moving to root)
          const draggedFileId = e.dataTransfer.getData('text/plain')
          if (draggedFileId && draggedFileId !== '') {
            // Recursively find the file in all nested folders
            const findFileById = (fileList: DocumentFile[], id: string): DocumentFile | null => {
              for (const file of fileList) {
                if (file.id === id) return file
                if (file.children && file.children.length > 0) {
                  const found = findFileById(file.children, id)
                  if (found) return found
                }
              }
              return null
            }
            
            const draggedFile = findFileById(files, draggedFileId)
            
            if (draggedFile) {
              // Move to root (empty string means root)
              await handleMoveFile(draggedFile, '')
            }
            setDragOverFolder(null)
            return
          }
          
          // Otherwise, handle file upload to root
          const droppedFiles = Array.from(e.dataTransfer.files)
          if (droppedFiles.length > 0) {
            await uploadFiles(droppedFiles, null)
          }
          setDragOverFolder(null)
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : !files || files.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
            <FolderIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No files yet</p>
            <p className="text-xs mt-1">Create a file or upload files to get started</p>
          </div>
        ) : (
          <div>
            {files.map((file) => {
              // console.log('[FileBrowser] Rendering file:', file)
              return (
                <FileTreeNode
                  key={file.id}
                  file={file}
                  level={0}
                  onFileSelect={handleFileSelect}
                  onFileDelete={handleFileDelete}
                  onRename={handleRename}
                  onMove={handleMoveFile}
                  onCopy={handleCopyFile}
                  onDownload={handleFileDownload}
                  selectedFileId={selectedFileId}
                  dragOverFolder={dragOverFolder}
                  onDragOverFolder={setDragOverFolder}
                />
              )
            })}
          </div>
        )}

        {isUploading && (
          <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-2 text-sm">Uploading...</p>
            </div>
          </div>
        )}
      </div>

      {/* Folder Selection Modal for Uploads */}
      <FolderSelectionModal
        isOpen={showFolderModal}
        onClose={() => {
          setShowFolderModal(false)
          setPendingFiles([])
        }}
        onSelect={handleFolderSelect}
        folders={collectFolders(files)}
        documentId={documentId}
        title="Select Upload Location"
      />

      {/* Move File Modal */}
      <FolderSelectionModal
        isOpen={moveFileModal.isOpen}
        onClose={() => {
          setMoveFileModal({ file: null, isOpen: false })
        }}
        onSelect={(folderId) => {
          if (moveFileModal.file) {
            handleMoveFile(moveFileModal.file, folderId)
          }
        }}
        folders={collectFoldersForMove(files, moveFileModal.file?.id)}
        documentId={documentId}
        title="Select Destination Folder"
      />

      {/* Copy File Modal */}
      <FolderSelectionModal
        isOpen={copyFileModal.isOpen}
        onClose={() => {
          setCopyFileModal({ file: null, isOpen: false })
        }}
        onSelect={(folderId) => {
          if (copyFileModal.file) {
            handleCopyFile(copyFileModal.file, folderId)
          }
        }}
        folders={collectFoldersForMove(files, copyFileModal.file?.id)}
        documentId={documentId}
        title="Select Destination Folder"
      />

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={!!imagePreview}
        onClose={() => {
          if (imagePreview?.url) {
            URL.revokeObjectURL(imagePreview.url)
          }
          setImagePreview(null)
        }}
        imageUrl={imagePreview?.url || ''}
        imageName={imagePreview?.name || ''}
      />

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={!!pdfPreview}
        onClose={() => {
          if (pdfPreview?.url) {
            URL.revokeObjectURL(pdfPreview.url)
          }
          setPdfPreview(null)
        }}
        pdfUrl={pdfPreview?.url || ''}
        pdfName={pdfPreview?.name || ''}
      />

      {/* File Version History Modal */}
      <FileVersionHistoryModal
        isOpen={versionHistoryModal.isOpen}
        onClose={() => setVersionHistoryModal({ isOpen: false, fileId: '', fileName: '' })}
        documentId={documentId}
        fileId={versionHistoryModal.fileId}
        fileName={versionHistoryModal.fileName}
        onVersionRestored={() => {
          loadFiles()
        }}
      />

      {/* Overwrite Confirmation Dialog */}
      <ConfirmDialog
        isOpen={overwriteConfirm.isOpen}
        onClose={handleOverwriteCancel}
        onConfirm={handleOverwriteConfirm}
        title="File Already Exists"
        message={`A file named "${overwriteConfirm.fileName}" already exists. Do you want to overwrite it?`}
        confirmText="Overwrite"
        cancelText="Cancel"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
      />
    </div>
  )
}

